import type { MessageDescriptor } from 'react-intl';

import messages from './messages';

/** The Performance tabs, in the PO's order (08.10.2026: Booking Sessions
 *  right after Trainings). The page AND the sidebar read this one list. */
export const PERFORMANCE_TABS = [
  'overview',
  'clients',
  'memberships',
  'revenue',
  'trainings',
  'sessions',
  'schedule',
  'retention',
] as const;
export type PerformanceTab = (typeof PERFORMANCE_TABS)[number];

export const PERFORMANCE_TAB_LABELS: Record<PerformanceTab, MessageDescriptor> =
  {
    clients: messages.tabClientsGroups,
    memberships: messages.tabMemberships,
    overview: messages.tabOverview,
    retention: messages.tabRetention,
    revenue: messages.tabRevenue,
    schedule: messages.tabSchedule,
    sessions: messages.tabBookingSessions,
    trainings: messages.tabTrainings,
  };

/**
 * Which tabs a reader gets — the ONE gate the page and the sidebar share.
 * `canReadOrgMoney` is the actor-class money predicate
 * (`useCanReadOrgMoney`), never `finance_stats_read` alone: an employed
 * trainer holding that key through a custom role gets no money tab, because
 * every request behind it is refused (B7).
 *
 * `sessionsOn` is the tenant's `sessions` switch as the reader experiences it
 * (`useTenantFeatures`, on for a platform user): the session statistics —
 * Booking Sessions, and Schedule's attendance by slot — are that feature's
 * own report and go with it. The read key they hang off,
 * `booking_events_read`, is deliberately KEPT while the switch is off
 * (existing sessions stay manageable), so the switch is asked here.
 */
export const performanceTabAccess = ({
  canReadOrgMoney,
  canReadSessions,
  canReadWork,
  sessionsOn,
}: {
  canReadOrgMoney: boolean;
  canReadSessions: boolean;
  canReadWork: boolean;
  sessionsOn: boolean;
}): Record<PerformanceTab, boolean> => ({
  clients: canReadWork,
  memberships: canReadOrgMoney,
  overview: canReadWork || canReadOrgMoney,
  retention: canReadWork,
  revenue: canReadOrgMoney,
  schedule: canReadSessions && sessionsOn,
  sessions: canReadSessions && sessionsOn,
  trainings: canReadWork,
});
